/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { assetBalancesArray, postData } from '../../redux/reducers/data';
import { setMessage } from '../../redux/actions/message';
import AccountLabel from '../../elements/AccountLabel';
import ToolTipOnlyIcon from '../../elements/ToolTipOnlyIcon';
import ToolTipNoIcon from '../../elements/ToolTipNoIcon';
import Button from '../../elements/Button';
import SublistOkCancel from './SublistOkCancel';
import {
  parseTags,
  getTransactionSign,
  getAmountToSplitAbsolute,
  getInitialRepaymentSplit,
  fetchLoanRepaymentSplitFromDescription,
  getCalculatedInstallmentForCurrentMonth,
} from './loanRepaymentSplitHelpers';
import { MonestrySpinner as Spinner } from '../../icons/remoteAssets';

export default function BlueTransactionsLoanRepayment({
  accountId,
  transactions,
  handleCloseAfterSave,
  handleCancel,
  loanAccountsForRepaymentTagging,
}) {
  const { t } = useTranslation('accountDetails', { keyPrefix: 'splitMenu' });
  const { t: tLabel } = useTranslation('accountDetails', { keyPrefix: 'deposits.labels' });
  const [loading, setLoading] = useState(false);
  const [isLoadingPrefill, setIsLoadingPrefill] = useState(true);
  const [currentInstallment, setCurrentInstallment] = useState(null);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const transaction = transactions?.[0] || null;
  const selectAssetBalances = useSelector(assetBalancesArray);

  const currentTags = useMemo(() => parseTags(transaction?.tags), [transaction]);
  const currentLoanAccountId = currentTags?.accountId || null;
  const availableLoanAccounts = loanAccountsForRepaymentTagging || [];

  const amountToSplitAbsolute = useMemo(
    () => getAmountToSplitAbsolute({
      currentTransaction: transaction,
      currentTags,
    }),
    [transaction, currentTags],
  );

  const initialSplit = useMemo(
    () => getInitialRepaymentSplit({
      currentTags,
      amountToSplitAbsolute,
    }),
    [currentTags, amountToSplitAbsolute],
  );

  const [principal, setPrincipal] = useState(initialSplit.principal);
  const [interest, setInterest] = useState(initialSplit.interest);

  const [selectedLoanAccountId, setSelectedLoanAccountId] = useState(() => {
    if (currentLoanAccountId && availableLoanAccounts.some((item) => item.id === currentLoanAccountId)) return currentLoanAccountId;
    return availableLoanAccounts[0]?.id || null;
  });

  useEffect(() => {
    if (currentLoanAccountId && availableLoanAccounts.some((item) => item.id === currentLoanAccountId)) {
      setSelectedLoanAccountId(currentLoanAccountId);
      return;
    }
    if (!selectedLoanAccountId && availableLoanAccounts.length > 0) {
      setSelectedLoanAccountId(availableLoanAccounts[0].id);
    }
  }, [currentLoanAccountId, availableLoanAccounts, selectedLoanAccountId]);

  useEffect(() => {
    setPrincipal(initialSplit.principal);
    setInterest(initialSplit.interest);
  }, [initialSplit]);

  useEffect(() => {
    let isMounted = true;

    async function runPrefill() {
      if (currentTags?.loanRepayment || !transaction) {
        if (isMounted) setIsLoadingPrefill(false);
        return;
      }

      try {
        const split = await fetchLoanRepaymentSplitFromDescription(transaction.description || '');
        if (split && isMounted) {
          setPrincipal(split.principal);
          setInterest(split.interest);
        }
      } catch (prefillError) {
        console.error('BlueTransactionsLoanRepayment: prefill failed', prefillError);
      } finally {
        if (isMounted) setIsLoadingPrefill(false);
      }
    }

    runPrefill();

    return () => {
      isMounted = false;
    };
  }, [currentTags?.loanRepayment, transaction]);

  const selectedLoanAccount = availableLoanAccounts.find((item) => item.id === selectedLoanAccountId) || null;

  const remainingAmount = amountToSplitAbsolute - (principal + interest);
  const amountTolerance = 0.00999;

  async function handleSave() {
    if (!transaction || !selectedLoanAccount) {
      setError(t('selectLoanAccount'));
      return;
    }

    if (Math.abs(remainingAmount) > amountTolerance) {
      setError(remainingAmount > 0 ? `${t('amountToAllocate')} ${remainingAmount.toFixed(2)}` : `${t('allocatedTooMuch')} ${Math.abs(remainingAmount).toFixed(2)}`);
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const tags = parseTags(transaction.tags);
      delete tags.assetId;

      const totalRepayment = principal + interest;
      const totalSignedAmount = getTransactionSign(transaction) * totalRepayment;

      const updatedTransaction = {
        ...transaction,
        label: 'investment-loanRepayment',
        accountCurrencyAmount: totalSignedAmount,
        tags: {
          ...tags,
          accountId: selectedLoanAccount.id,
          loanRepayment: {
            principal,
            interest,
          },
        },
        linkedTransactions: (transaction.linkedTransactions || []).filter(
          (linkedTransaction) => !['fee-to-transaction', 'sale-to-transaction', 'transfer'].includes(linkedTransaction.tags?.linkType),
        ),
        importFlag: 'put',
        parentId: null,
      };

      await dispatch(
        postData({
          data: [updatedTransaction],
          category: 'deposits',
          accountId,
        }),
      );

      setLoading(false);
      handleCloseAfterSave();
    } catch (saveError) {
      setLoading(false);
      dispatch(setMessage('dataUpdateError'));
    }
  }

  function handleApplyCalculatedInstallment() {
    if (!selectedLoanAccount) return;
    const loanAccountBalanceObject = selectAssetBalances.find((item) => item.accountId === selectedLoanAccount.id) || { current: { quantity: 0 } };

    const calculatedInstallment = getCalculatedInstallmentForCurrentMonth({
      selectedLoanAccount,
      currentLoanBalance: loanAccountBalanceObject.current.quantity,
    });

    if (!calculatedInstallment) return;

    setCurrentInstallment(calculatedInstallment);
    setPrincipal(calculatedInstallment.principal);
    setInterest(calculatedInstallment.interest);
  }

  if (!transaction) return null;

  if (isLoadingPrefill) {
    return (
      <div className="flex w-full h-full items-center justify-center" data-testid="blue-transactions-loan-repayment-loading">
        <Spinner width="40" height="40" viewBox="0 0 600 600" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2 overflow-y-auto" data-testid="blue-transactions-loan-repayment">
        {availableLoanAccounts.map((loanAccount) => (
          <button
            type="button"
            key={loanAccount.id}
            onClick={() => {
              setSelectedLoanAccountId(loanAccount.id);
              setCurrentInstallment(null);
            }}
            data-testid="sublist-account"
            className={`text-xs border border-gray-200 shadow-sm rounded-md p-2 flex justify-between gap-2 cursor-pointer
              ${loanAccount.id === selectedLoanAccountId ? 'bg-brandBlue-500 text-white hover:bg-brandBlue-600 border-transparent' : 'bg-white text-gray-900 hover:bg-gray-50'}`}
          >
            <AccountLabel accountName={loanAccount?.name} category={loanAccount?.category} />
          </button>
        ))}

        <h3 className="mt-2 text-base font-medium text-gray-500">{t('separateInterestFromPrincipal')}</h3>

        <ToolTipNoIcon info={t('splitAndLabelLoanInfo2')} classNamesOwn="flex gap-2 mt-2">
          <div className="flex gap-2 items-center">
            <p className="text-sm text-gray-500">{t('splitAndLabelLoanInfo')}</p>
            <ToolTipOnlyIcon />
          </div>
        </ToolTipNoIcon>

        <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
          <p className="text-xs font-medium text-gray-500">{t('transactionDescriptionLabel')}</p>
          <p className="mt-1 text-sm text-gray-700 break-words">
            {transaction.description || t('transactionDescriptionEmpty')}
          </p>
        </div>

        <ul className="mt-2 divide divide-gray-100">
          {[
            { label: 'investment-loanPrincipal', value: principal, onChange: setPrincipal },
            { label: 'investment-loanInterest', value: interest, onChange: setInterest },
          ].map((item) => (
            <li className="grid grid-cols-12 items-center p-2 gap-4 text-sm" key={item.label}>
              <span className="col-span-4 inline-flex rounded-lg bg-brandBlue-100 hover:bg-brandBlue-200 px-2.5 py-0.5 text-xs font-medium text-brandBlue-700">
                {tLabel(`${item.label}.short`)}
              </span>
              <input
                className="self-start px-1.5 py-2 text-gray-700 text-right border-b rounded-md border-gray-300 focus:outline-none focus:border-brandBlue-400 col-span-8"
                value={item.value}
                onChange={(e) => item.onChange(Number.isNaN(Number(e.target.value)) ? 0 : Math.abs(Number(e.target.value)))}
              />
            </li>
          ))}
        </ul>

        {remainingAmount > amountTolerance && <p className="mt-2 text-sm text-red-500">{`${t('amountToAllocate')} ${remainingAmount.toFixed(2)}`}</p>}
        {remainingAmount < -amountTolerance && <p className="mt-2 text-sm text-red-500">{`${t('allocatedTooMuch')} ${Math.abs(remainingAmount).toFixed(2)}`}</p>}
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

        {currentInstallment ? (
          <button className="text-sm text-gray-500 cursor-pointer underline mt-1" type="button" onClick={handleApplyCalculatedInstallment}>
            {`${tLabel('investment-loanPrincipal.short')}: ${currentInstallment.principal.toFixed(2)} | ${tLabel('investment-loanInterest.short')}: ${currentInstallment.interest.toFixed(2)}`}
          </button>
        ) : (
          <ToolTipNoIcon info={t('calculateExpectedSplitInfo')}>
            <Button onClick={handleApplyCalculatedInstallment} formatting="text-brandBlue-500" text={t('calculateExpectedSplit')} size="sm" noFrame />
          </ToolTipNoIcon>
        )}
      </div>
      <SublistOkCancel handleSave={handleSave} handleCancel={handleCancel} loading={loading} />
    </>
  );
}

BlueTransactionsLoanRepayment.propTypes = {
  accountId: PropTypes.string.isRequired,
  handleCloseAfterSave: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  loanAccountsForRepaymentTagging: PropTypes.arrayOf(PropTypes.object),
  transactions: PropTypes.arrayOf(PropTypes.object).isRequired,
};

BlueTransactionsLoanRepayment.defaultProps = {
  loanAccountsForRepaymentTagging: [],
};
